/**
 * TransformController — wraps GizmoManager to provide translate/rotate/scale
 * gizmos. Drag start/move/end are surfaced via callbacks so the Editor can drive
 * the shared 'element:transforming'/'element:transformed' event flow.
 */

import { GizmoManager, Tools, type Scene, type TransformNode, type Observer, type Nullable } from 'babylonjs';

export type TransformMode = 'translate' | 'rotate' | 'scale';

// Babylon's default gizmos are thin/small; scale them up to be clearly visible.
const GIZMO_SCALE_RATIO = 2.5;

export interface TransformCallbacks {
  onDragStart(node: TransformNode): void;
  onDrag(node: TransformNode): void;
  onDragEnd(node: TransformNode): void;
}

export class TransformController {
  private scene: Scene;
  private gizmoManager: GizmoManager;
  private callbacks: TransformCallbacks;
  private mode: TransformMode = 'translate';
  private localSpace = true;
  private snapping = false;
  private dragging = false;
  private attached: Nullable<TransformNode> = null;
  private renderObserver: Observer<Scene> | null = null;

  constructor(scene: Scene, callbacks: TransformCallbacks) {
    this.scene = scene;
    this.callbacks = callbacks;
    this.gizmoManager = new GizmoManager(scene);
    this.gizmoManager.usePointerToAttachGizmos = false; // selection-driven, not click-on-mesh
    this.gizmoManager.positionGizmoEnabled = true;
    this.gizmoManager.rotationGizmoEnabled = false;
    this.gizmoManager.scaleGizmoEnabled = false;
    this._applySpace();
    this._applyScaleRatio();
    this._bindGizmoObservers();
  }

  setMode(mode: TransformMode): void {
    this.mode = mode;
    this.gizmoManager.positionGizmoEnabled = mode === 'translate';
    this.gizmoManager.rotationGizmoEnabled = mode === 'rotate';
    this.gizmoManager.scaleGizmoEnabled = mode === 'scale';
    this._applySpace();
    this._applySnapping();
    this._applyScaleRatio();
    this._bindGizmoObservers();
    // Re-attach so the newly-enabled gizmo binds to the current node.
    if (this.attached) this.gizmoManager.attachToNode(this.attached);
  }

  setSpace(space: 'world' | 'local'): void {
    this.localSpace = space === 'local';
    this._applySpace();
  }

  setSnapping(enabled: boolean): void {
    this.snapping = enabled;
    this._applySnapping();
  }

  attach(node: TransformNode): void {
    this.attached = node;
    this.gizmoManager.attachToNode(node);
    this._bindGizmoObservers();
  }

  detach(): void {
    this.attached = null;
    this.gizmoManager.attachToNode(null);
  }

  set enabled(value: boolean) {
    // Hide/show by detaching; preserve the attached reference.
    this.gizmoManager.attachToNode(value ? this.attached : null);
  }

  private _applySpace(): void {
    const g = this.gizmoManager.gizmos;
    // Position gizmo always uses WORLD axes — the move arrows stay locked to the
    // scene's X/Y/Z regardless of how the element is rotated. (Following the
    // element's local frame made dragging a rotated/pasted element move along its
    // tilted axes, which is confusing.)
    if (g.positionGizmo) g.positionGizmo.updateGizmoRotationToMatchAttachedMesh = false;
    // Rotation gizmo always uses WORLD axes. Panels carry a non-uniform scale
    // ([w,h,1]); a local-space rotation gizmo aligns its rings to the mesh's
    // sheared local frame, where Babylon's drag-plane math degenerates and the
    // panel won't rotate (models work only because their anchor is uniform).
    // World axes are scale-independent and rotate reliably. Matches the GUI,
    // which also forces world space when rotating.
    if (g.rotationGizmo) g.rotationGizmo.updateGizmoRotationToMatchAttachedMesh = false;
    if (g.scaleGizmo) g.scaleGizmo.updateGizmoRotationToMatchAttachedMesh = true; // scale is always local
  }

  private _applySnapping(): void {
    const g = this.gizmoManager.gizmos;
    if (g.positionGizmo) g.positionGizmo.snapDistance = this.snapping ? 0.1 : 0;
    if (g.rotationGizmo) g.rotationGizmo.snapDistance = this.snapping ? Tools.ToRadians(15) : 0;
    if (g.scaleGizmo) g.scaleGizmo.snapDistance = this.snapping ? 0.1 : 0;
  }

  private _applyScaleRatio(): void {
    const g = this.gizmoManager.gizmos;
    if (g.positionGizmo) g.positionGizmo.scaleRatio = GIZMO_SCALE_RATIO;
    if (g.rotationGizmo) g.rotationGizmo.scaleRatio = GIZMO_SCALE_RATIO;
    if (g.scaleGizmo) g.scaleGizmo.scaleRatio = GIZMO_SCALE_RATIO;
  }

  /** Bind drag start/end to whichever gizmo is currently active. */
  private _bindGizmoObservers(): void {
    const g = this.gizmoManager.gizmos;
    const active = g.positionGizmo ?? g.rotationGizmo ?? g.scaleGizmo;
    const current = this.mode === 'translate' ? g.positionGizmo
      : this.mode === 'rotate' ? g.rotationGizmo : g.scaleGizmo;
    const gizmo = current ?? active;
    if (!gizmo) return;

    gizmo.onDragStartObservable.clear();
    gizmo.onDragEndObservable.clear();
    gizmo.onDragStartObservable.add(() => this._onDragStart());
    gizmo.onDragEndObservable.add(() => this._onDragEnd());
  }

  private _onDragStart(): void {
    if (!this.attached) return;
    this.dragging = true;
    this.callbacks.onDragStart(this.attached);
    // Emit continuous updates while dragging.
    this.renderObserver = this.scene.onBeforeRenderObservable.add(() => {
      if (this.dragging && this.attached) this.callbacks.onDrag(this.attached);
    });
  }

  private _onDragEnd(): void {
    this.dragging = false;
    if (this.renderObserver) {
      this.scene.onBeforeRenderObservable.remove(this.renderObserver);
      this.renderObserver = null;
    }
    if (this.attached) this.callbacks.onDragEnd(this.attached);
  }

  dispose(): void {
    if (this.renderObserver) {
      this.scene.onBeforeRenderObservable.remove(this.renderObserver);
      this.renderObserver = null;
    }
    this.gizmoManager.dispose();
  }
}

export default TransformController;
